import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    commitStandardApply,
    getStandardApplyRun,
    listStandardApplyRuns,
    previewStandardApply,
    resolveStandardApply,
} from '../../../../services/epcStandardScheduleService';
import type { ApplyCommitBody, ApplyPreviewBody } from '../types';

const APPLY_RUNS_ROOT = (projectId: string) =>
    ['epc', 'standard-apply-runs', projectId] as const;

/** resolve → the standard the project resolves to (or null + reason). Gate with `enabled`. */
export function useResolveStandardApply(projectId: string, enabled = true) {
    return useQuery({
        queryKey: ['epc', 'standard-apply-resolve', projectId],
        queryFn: () => resolveStandardApply(projectId),
        enabled: enabled && !!projectId,
    });
}

/** preview is a POST (options → dated tree). Mutation so callers debounce + hold the result. */
export function usePreviewStandardApply(projectId: string) {
    return useMutation({
        mutationFn: (body: ApplyPreviewBody) => previewStandardApply(projectId, body),
    });
}

/** commit — carries the full preview inputs + applyId. Invalidates schedule/day-plan/runs. */
export function useCommitStandardApply(projectId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: ApplyCommitBody) => commitStandardApply(projectId, body),
        onSuccess: () => {
            // baseline day plans + materialised schedule/import + the runs list all change
            qc.invalidateQueries({ queryKey: ['epc-dm', 'day-plan', projectId] });
            qc.invalidateQueries({ queryKey: ['epc-dm', 'workspace', projectId] });
            qc.invalidateQueries({ queryKey: ['epc-project', projectId] });
            qc.invalidateQueries({ queryKey: APPLY_RUNS_ROOT(projectId) });
        },
    });
}

export function useStandardApplyRuns(projectId: string, enabled = true) {
    return useQuery({
        queryKey: APPLY_RUNS_ROOT(projectId),
        queryFn: () => listStandardApplyRuns(projectId),
        enabled: enabled && !!projectId,
    });
}

export function useStandardApplyRun(projectId: string, runId: string | null) {
    return useQuery({
        queryKey: ['epc', 'standard-apply-run', projectId, runId],
        queryFn: () => getStandardApplyRun(projectId, runId as string),
        enabled: !!projectId && !!runId,
    });
}
